import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OutreachSender } from '@/types/outreach';
import { isSameSenderName } from '@/types/outreach';

export interface CampaignSenderResult {
  /** `sending_personas.full_name` for the campaign's persona, if any. */
  senderName: string | null;
  /** Matching `team_profiles.id`, if the persona maps to a known sender. */
  senderId: string | null;
}

/**
 * Resolves which sender owns a campaign: campaign → sending_persona → team_profiles sender.
 *
 * Shared by LinkedInFollowUpPanel and LinkedInSecondTouchPanel so the two panels
 * cannot drift apart on ownership rules.
 *
 * Matching is done on CANONICAL names via `isSameSenderName`. An earlier version
 * compared the persona's full_name against the sender's DISPLAY name, so any rep
 * with an alias (Jacob Andrews / 'Jake Andrews') never matched their own campaign.
 */
export function useCampaignSender(
  campaignId: string | null | undefined,
  senders: OutreachSender[],
) {
  return useQuery({
    queryKey: ['campaign-sender', campaignId],
    queryFn: async (): Promise<CampaignSenderResult> => {
      if (!campaignId) return { senderName: null, senderId: null };

      const { data: campaign, error: cErr } = await supabase
        .from('campaigns')
        .select('sending_persona_id')
        .eq('id', campaignId)
        .maybeSingle();

      if (cErr || !campaign) return { senderName: null, senderId: null };
      const personaId = (campaign as any).sending_persona_id as string | null;
      if (!personaId) return { senderName: null, senderId: null };

      const { data: persona, error: pErr } = await supabase
        .from('sending_personas')
        .select('full_name')
        .eq('id', personaId)
        .maybeSingle();

      if (pErr || !persona) return { senderName: null, senderId: null };
      const fullName = (persona as any).full_name as string;

      const matched = senders.find((s) => isSameSenderName(s.canonicalName, fullName));
      return { senderName: fullName, senderId: matched?.id || null };
    },
    enabled: !!campaignId && senders.length > 0,
    staleTime: 10 * 60 * 1000,
  });
}
